/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-05
 */

// Add third-party dependencies.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  Zoom,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

// Add local dependencies.
import { usePasswordResetConfirmMutation } from '../../../features/auth';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { passwordResetConfirmSchema } from '../../../utils/validationSchemas';
import {
  validatePasswordStrength,
  getPasswordStrengthScore,
  getPasswordStrengthLabel,
  getPasswordStrengthColor,
} from '../../../utils/passwordStrength';
import { useSuccessNotification } from '../../../hooks/useSuccessNotification';

interface FormData {
  password: string;
  confirmPassword: string;
}

/**
 * Password reset confirmation form component.
 * Allows users to set a new password using a reset token.
 * @example
 * return <PasswordResetConfirmForm />
 * @returns {React.ReactElement} The password reset confirmation form component.
 */
const PasswordResetConfirmForm: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [passwordResetConfirm, { isLoading }] =
    usePasswordResetConfirmMutation();
  const { showPasswordResetSuccess } = useSuccessNotification();

  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
  });

  const [apiError, setApiError] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const [token, setToken] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    password: 'idle' | 'valid' | 'invalid';
    confirmPassword: 'idle' | 'valid' | 'invalid';
  }>({
    password: 'idle',
    confirmPassword: 'idle',
  });

  // Initialize form validation with password reset confirm schema
  const {
    errors,
    validateForm,
    handleInputChange: handleValidationChange,
  } = useFormValidation<FormData>(passwordResetConfirmSchema, {
    validateOnBlur: true,
  });

  // Extract token from URL parameters
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setApiError(
        'Invalid or missing reset token. Please request a new password reset.'
      );
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Use validation hook for error handling
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    handleValidationChange(name as keyof FormData, value, updatedFormData);

    // Update field validation states for visual feedback
    if (name === 'password' || name === 'confirmPassword') {
      const fieldName = name as 'password' | 'confirmPassword';
      const hasError = errors[fieldName];
      const hasValue = value.trim().length > 0;

      let validationState: 'idle' | 'valid' | 'invalid' = 'idle';
      if (hasError) {
        validationState = 'invalid';
      } else if (hasValue) {
        validationState = 'valid';
      }

      setFieldValidationStates(prev => ({
        ...prev,
        [fieldName]: validationState,
      }));
    }
  };

  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: 'password' | 'confirmPassword') => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setIsSubmitting(true);

    if (!token) {
      setApiError(
        'Invalid or missing reset token. Please request a new password reset.'
      );
      setIsSubmitting(false);
      return;
    }

    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    try {
      await passwordResetConfirm({
        token,
        new_password: formData.password,
        new_password_confirm: formData.confirmPassword,
      }).unwrap();

      // Show success notification with action button
      showPasswordResetSuccess();

      // Clear form
      setFormData({
        password: '',
        confirmPassword: '',
      });
      setFieldValidationStates({
        password: 'idle',
        confirmPassword: 'idle',
      });

      // Redirect to login after 5 seconds to give user time to see notification
      setTimeout(() => {
        navigate('/login', {
          state: {
            message:
              'Password reset successful. Please sign in with your new password.',
          },
        });
      }, 5000);
    } catch (error: any) {
      if (error.data?.new_password) {
        setApiError(error.data.new_password[0]);
      } else if (error.data?.password) {
        setApiError(error.data.password[0]);
      } else if (error.data?.token) {
        setApiError(
          'Invalid or expired reset token. Please request a new password reset.'
        );
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPasswordStrength = () => {
    if (!formData.password) return null;

    const passwordErrors = validatePasswordStrength(formData.password);
    const strength = getPasswordStrengthScore(formData.password);

    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('passwordReset.confirm.passwordStrength.label')}{' '}
          {getPasswordStrengthLabel(strength)}
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: 4,
            backgroundColor: '#e0e0e0',
            borderRadius: 2,
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              width: `${(strength / 4) * 100}%`,
              height: '100%',
              backgroundColor: getPasswordStrengthColor(strength),
              borderRadius: 2,
              transition: 'all 0.3s ease',
            }}
          />
        </Box>
        {passwordErrors.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {passwordErrors.map(error => (
              <Typography
                key={error}
                variant="caption"
                color="error"
                display="block"
              >
                • {error}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  if (!token && !apiError) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('passwordReset.confirm.loading.verifying')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Loading backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
        open={isLoading && isSubmitting}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {t('passwordReset.confirm.loading.resetting')}
        </Typography>
      </Backdrop>

      {/* Progress bar */}
      {(isLoading || isSubmitting) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme => theme.zIndex.appBar + 1,
          }}
        />
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('passwordReset.confirm.title')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('passwordReset.confirm.description')}
        </Typography>

        <Fade in={!!apiError}>
          <Box>
            {apiError && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
              >
                {apiError}
              </Alert>
            )}
          </Box>
        </Fade>

        <TextField
          fullWidth
          label={t('passwordReset.confirm.form.label.newPassword')}
          name="password"
          type={showPasswords ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange}
          error={!!errors.password}
          helperText={errors.password}
          disabled={isLoading || isSubmitting}
          required
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('password')}
                <IconButton
                  onClick={() => setShowPasswords(!showPasswords)}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  aria-label={t(
                    'passwordReset.confirm.accessibility.passwordToggle'
                  )}
                  sx={{ ml: 0.5 }}
                >
                  {showPasswords ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {renderPasswordStrength()}

        <TextField
          fullWidth
          label={t('passwordReset.confirm.form.label.confirmPassword')}
          name="confirmPassword"
          type={showPasswords ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleInputChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          disabled={isLoading || isSubmitting}
          required
          autoComplete="new-password"
          InputProps={{
            endAdornment: getValidationIcon('confirmPassword'),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading || isSubmitting || !token}
          sx={{
            mt: 1,
            mb: 3,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {isLoading || isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('passwordReset.confirm.form.submit')
          )}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2">
            {t('passwordReset.confirm.links.rememberPassword')}{' '}
            <Link
              to="/login"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {t('passwordReset.confirm.links.signIn')}
            </Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PasswordResetConfirmForm;
